import React from 'react';
import { Box, keyframes, useTheme } from '@mui/material';
import inxitsLogo from '../../assets/Inxits.svg';

const spin = keyframes`
  0%   { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50%       { opacity: 0.75; }
`;

const InxitsLoader = ({ size = 120, message = '' }) => {
    const theme = useTheme();

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 2,
            }}
        >
            <Box sx={{ position: 'relative', width: size, height: size }}>

                {/* Spinning SVG arc */}
                <Box
                    component="svg"
                    viewBox="0 0 100 100"
                    sx={{
                        position: 'absolute',
                        top: 0, left: 0,
                        width: '100%',
                        height: '100%',
                        animation: `${spin} 2s linear infinite`,
                    }}
                >
                    <defs>
                        {/*
              Gradient: starts dark navy (arc tail) → bright blue (arc head)
              x1/y1/x2/y2 are in 0-1 range, roughly top-left to bottom-right
              to match the Figma arc direction
            */}
                        <linearGradient id="arcGradient" x1="1" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor="#1A3A5C" stopOpacity="0.2" />
                            <stop offset="60%" stopColor="#1B6CA8" stopOpacity="0.85" />
                            <stop offset="100%" stopColor="#46A8DC" stopOpacity="1" />
                        </linearGradient>
                    </defs>

                    {/*
            Background track arc — dark, almost full circle
            gives the "ring" feel from Figma
          */}
                    <circle
                        cx="50"
                        cy="50"
                        r="44"
                        fill="none"
                        stroke="#1A2C45"
                        strokeWidth="5"
                        strokeOpacity="0.6"
                    />

                    {/*
            Main gradient arc — open gap at bottom (~30deg gap)
            Starts at top (270deg), goes clockwise, ends ~240deg
            M x y = start point at 270deg: (50, 6)
            Arc endpoint at 240deg: x=50+44*cos(240)=50-22=28, y=50+44*sin(240)=50-38.1=11.9
            large-arc-flag=1 to sweep most of circle
            sweep-flag=1 = clockwise
          */}
                    <path
                        d="M 50 6 A 44 44 0 1 1 28 12"
                        fill="none"
                        stroke="url(#arcGradient)"
                        strokeWidth="5"
                        strokeLinecap="round"
                    />
                </Box>

                {/* Center logo — no pulse, stays solid like Figma */}
                <Box
                    component="img"
                    src={inxitsLogo}
                    alt="Inxits"
                    sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: size * 0.48,
                        height: size * 0.48,
                        objectFit: 'contain',
                        animation: `${pulse} 2s ease-in-out infinite`,
                    }}
                />
            </Box>

            {message && (
                <Box
                    sx={{
                        color: theme.palette.text.disabled,
                        fontSize: theme.typography.custom?.label?.fontSize || '0.85rem',
                        letterSpacing: '0.05em',
                    }}
                >
                    {message}
                </Box>
            )}
        </Box>
    );
};

export default InxitsLoader;